/**
 * Satisfaction scores — manually recorded, unlike every other metric on this
 * page.
 *
 * The question is "if Hourglass went away, how would users feel?", so the scale
 * is INVERTED relative to intuition: 1 (very disappointed) is the good end and
 * 4 (very happy) is the bad one. Nothing here should sort or colour on the
 * assumption that higher is better.
 *
 * Backend: /admin/satisfaction, gated by requireHourglassInternal.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "@/shared/utils/request";

/** 1 = very disappointed (best) … 4 = very happy (worst). */
export type SatisfactionScore = 1 | 2 | 3 | 4;

export const SATISFACTION_LABELS: Record<SatisfactionScore, string> = {
	1: "Very disappointed",
	2: "Somewhat disappointed",
	3: "Somewhat happy",
	4: "Very happy",
};

export const SATISFACTION_SCORES: SatisfactionScore[] = [1, 2, 3, 4];

/**
 * 1-2 good, 3-4 bad. Binary, not banded — a four-point forced choice has no
 * meaningful middle. Null is a note-only row, rendered neutrally.
 */
export function satisfactionClass(score: number | null): string {
	if (score === null) return "bg-muted text-muted-foreground";
	return score <= 2
		? "bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300"
		: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300";
}

export function isSatisfactionScore(n: number): n is SatisfactionScore {
	return Number.isInteger(n) && n >= 1 && n <= 4;
}

/** Wire shape from the API — camelCase, `createdAt` is a full ISO timestamp. */
export interface SatisfactionEntry {
	id: string;
	profId: string;
	score: number | null;
	note: string | null;
	createdAt: string;
}

/**
 * A row reached through the SCORE ranking (`WHERE score IS NOT NULL`), so its
 * score is guaranteed. Its note is not — a score can be recorded without one.
 */
export interface ScoredEntry extends SatisfactionEntry {
	score: number;
}

/**
 * A row reached through the NOTE ranking (`WHERE note IS NOT NULL`), so its
 * note is guaranteed. Its score is not — this may be a note-only row, and it
 * may or may not be the same row as `current`.
 */
export interface NotedEntry extends SatisfactionEntry {
	note: string;
}

/**
 * Scores and notes are ranked SEPARATELY by the server, over two different
 * filtered sets: current/previous are the latest two rows carrying a score,
 * latestNote is the latest row carrying a note.
 *
 * That separation is what stops a note-only row from blanking the score
 * column — ranking both off "newest row" would make writing a note today erase
 * yesterday's score from the table.
 *
 * All three are independently nullable. A timekeeper may have notes and no
 * score, a score and no notes, or one score and no previous.
 */
export interface SatisfactionPair {
	current: ScoredEntry | null;
	previous: ScoredEntry | null;
	latestNote: NotedEntry | null;
}

export type SatisfactionMap = Record<string, SatisfactionPair>;

const KEY = ["internal", "satisfaction"] as const;

export function useSatisfaction(profIds: string[]) {
	const { makeApiRequest } = useApi();
	const queryClient = useQueryClient();

	// Sorted and joined so a reordering upstream doesn't refetch.
	const profIdsKey = [...profIds].sort().join(",");

	const query = useQuery({
		queryKey: [...KEY, profIdsKey] as const,
		enabled: profIdsKey.length > 0,
		queryFn: async ({ signal }) => {
			const res = await makeApiRequest({
				endpoint: `/admin/satisfaction?profIds=${encodeURIComponent(profIdsKey)}`,
				method: "GET",
				otherOptions: { signal },
			});
			if (res.error) throw new Error(String(res.error));
			return (res.data ?? {}) as SatisfactionMap;
		},
	});

	const mutation = useMutation({
		mutationFn: async (input: {
			profId: string;
			score: SatisfactionScore;
			note?: string | null;
		}) => {
			const res = await makeApiRequest({
				endpoint: "/admin/satisfaction",
				method: "POST",
				body: {
					prof_id: input.profId,
					score: input.score,
					note: input.note ?? null,
				},
			});
			if (res.error) throw new Error(String(res.error));
			return res.data as SatisfactionEntry;
		},
		// POST returns only the created row, not the new current/previous pair,
		// so the list has to be refetched to re-rank them.
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: KEY });
		},
	});

	/**
	 * Edit the note on the CURRENT entry.
	 *
	 * PATCH, never POST: a note is context for the score that already exists,
	 * not a new observation. Inserting a row would push the real previous score
	 * out of the comparison and make the table read as though the score had
	 * just changed when only its note did.
	 */
	const updateNote = useMutation({
		mutationFn: async (input: { entryId: string | null; profId: string; note: string }) => {
			const note = input.note.trim() === "" ? null : input.note.trim();

			// PATCH the row the note already lives on, so editing text never spawns
			// a history entry. With no note row yet, POST a note-only row — the
			// server ranks it out of the score columns, so it cannot disturb them.
			const res = input.entryId
				? await makeApiRequest({
						endpoint: `/admin/satisfaction/${encodeURIComponent(input.entryId)}`,
						method: "PATCH",
						body: { note },
					})
				: await makeApiRequest({
						endpoint: "/admin/satisfaction",
						method: "POST",
						body: { prof_id: input.profId, note },
					});

			if (res.error) throw new Error(String(res.error));
			return res.data as SatisfactionEntry;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: KEY });
		},
	});

	/**
	 * Record a score, but only when it actually changed.
	 *
	 * Re-entering the same number is a no-op: the table is a history of
	 * CHANGES, and writing an identical row would add a meaningless entry and
	 * reset the "previous" comparison to today.
	 */
	const record = useCallback(
		async (profId: string, score: SatisfactionScore) => {
			const currentScore = query.data?.[profId]?.current?.score;
			if (currentScore === score) return false;
			await mutation.mutateAsync({ profId, score });
			return true;
		},
		[query.data, mutation],
	);

	/**
	 * True when clearing this timekeeper's note would leave a row carrying
	 * neither a score nor a note.
	 *
	 * The server rejects that (400) rather than accumulating rows invisible to
	 * both rankings. The UI blocks it up front instead of surfacing the error,
	 * since the real remedy — deleting the row — is not exposed.
	 */
	const clearingWouldEmptyRow = useCallback(
		(profId: string, note: string) => {
			const latest = query.data?.[profId]?.latestNote;
			return (
				note.trim() === "" && !!latest && latest.score === null
			);
		},
		[query.data],
	);

	const saveNote = useCallback(
		async (profId: string, note: string) => {
			// Deliberately allowed with no score: a note can be the first thing
			// recorded about a timekeeper.
			if (clearingWouldEmptyRow(profId, note)) return false;
			const entryId = query.data?.[profId]?.latestNote?.id ?? null;
			await updateNote.mutateAsync({ entryId, profId, note });
			return true;
		},
		[query.data, updateNote, clearingWouldEmptyRow],
	);

	return {
		scores: query.data ?? {},
		isLoading: query.isLoading,
		error: query.error instanceof Error ? query.error.message : null,
		record,
		saveNote,
		clearingWouldEmptyRow,
		isSaving: mutation.isPending,
		isSavingNote: updateNote.isPending,
		saveError:
			mutation.error instanceof Error
				? mutation.error.message
				: updateNote.error instanceof Error
					? updateNote.error.message
					: null,
	};
}

/** Full history for one timekeeper, newest first. Fetched only when opened. */
export function useSatisfactionHistory(profId: string | null) {
	const { makeApiRequest } = useApi();

	return useQuery({
		queryKey: [...KEY, "history", profId] as const,
		enabled: !!profId,
		queryFn: async ({ signal }) => {
			const res = await makeApiRequest({
				endpoint: `/admin/satisfaction/${encodeURIComponent(profId ?? "")}/history`,
				method: "GET",
				otherOptions: { signal },
			});
			if (res.error) throw new Error(String(res.error));
			return (res.data ?? []) as SatisfactionEntry[];
		},
	});
}
